import { type Ref } from 'react';

import { omit } from '@craft/lib/primitives';

import {
  useItemFocusedState,
  type UseItemFocusedStateOptions,
} from './internalHooks/accessibility';
import {
  GenericListItemsContainer,
  GenericMenuListItemsStack,
  type GenericMenuListItemsStackProps,
  type ListItemsContainerProps,
} from './structural';

export interface GenericMenuListProps
  extends
    UseItemFocusedStateOptions,
    Omit<ListItemsContainerProps, 'gap' | 'onChange' | 'onKeyDown' | 'ref'>,
    Pick<
      GenericMenuListItemsStackProps,
      'aria-label' | 'itemHeight' | 'minItemHeight' | 'onKeyDown'
    > {
  readonly ref?: Ref<HTMLDivElement>;
}

export const GenericMenuList = ({
  autoFocusItem,
  children,
  id,
  itemHeight,
  minItemHeight,
  onKeyDown,
  ref,
  ...props
}: GenericMenuListProps) => {
  const { handleKeyDown, listRef } = useItemFocusedState({ autoFocusItem });

  return (
    <GenericListItemsContainer {...omit(props, ['aria-label'])} ref={ref}>
      <GenericMenuListItemsStack
        aria-label={props['aria-label']}
        id={id}
        itemHeight={itemHeight}
        minItemHeight={minItemHeight}
        onKeyDown={e => {
          onKeyDown?.(e);
          handleKeyDown(e);
        }}
        ref={listRef}
      >
        {children}
      </GenericMenuListItemsStack>
    </GenericListItemsContainer>
  );
};
